'use client';

import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useServerInsertedHTML } from 'next/navigation';
import { StyleSheet, View } from 'react-native';
import { Provider } from 'react-redux';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { RouterBridge } from 'expo-router';
import { store } from '@/store';
import { useAppDispatch } from '@/store/hooks';
import { useSyncGenderFilter } from '@/hooks/useSyncGenderFilter';
import { useApplyPendingReferral } from '@/hooks/useApplyPendingReferral';
import { bootstrapAuth } from '@/store/slices/authSlice';
import { loadFavorites } from '@/store/slices/favoritesSlice';
import { loadPreferences } from '@/store/slices/preferencesSlice';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { ToastHost } from '@/components/ui/ToastHost';
import { AlertHost } from '@/components/ui/AppAlert';
import { ResponsiveFrame } from '@/components/ui/ResponsiveFrame';
import { InstallPrompt } from '@/components/ui/InstallPrompt';
import { registerServiceWorker } from '@/utils/pwa';
import { ThemeProvider, useAppTheme } from '@/theme';
import { API_BASE_URL } from '@/config/api';
import { installClientKey } from '@/config/clientKey';
import { installWebRefreshCookie } from '@/config/webSession';

// Every request to the API carries this site's client id and key (server: client-key.middleware.ts). The website's root is this
// file, not src/app/_layout.tsx, so it is installed here, before any request is made.
installClientKey();
// Browser build: keep the refresh token in an HttpOnly cookie where the browser allows it (utils/refreshCookieFetch.ts). Before any request.
installWebRefreshCookie();

/*
 * What the app's root layout (src/app/_layout.tsx) does on a phone, for Next.js: the Redux store,
 * the theme, the signed-in session, the chat connection, and the frame every screen sits in. The
 * screens themselves are the app's own files, unchanged.
 */

// The store as it is before any session is restored. The server renders from this, so the browser's first
// (hydrating) render must read it too; otherwise a screen that hydrates after the saved session has loaded
// shows the signed-in view against signed-out HTML. React then updates to the live state right after.
const INITIAL_STATE = store.getState();

// The API host sleeps when idle and can take up to a minute to wake. Knock once as the site opens.
if (typeof window !== 'undefined' && API_BASE_URL) void fetch(`${API_BASE_URL}/health`).catch((): void => undefined);

/** Splits a stylesheet's text into its top-level rules (an @keyframes or @media block counts as one). */
function splitRules(css: string): string[] {
  const rules: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < css.length; i += 1) {
    const ch = css[i];
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        rules.push(css.slice(start, i + 1));
        start = i + 1;
      }
    }
  }
  return rules;
}

/**
 * react-native-web's styles, written into the HTML on the server so the page is styled before any script
 * runs. Next streams the page in several chunks and asks for the styles after each one; every ask used to
 * repeat the whole sheet (four copies, about 100 KB of HTML). Each rule is now sent once: the first chunk
 * carries the sheet the browser adopts (by its id), later chunks only the rules that are new.
 */
function ServerStyles(): null {
  const sent = useRef(new Set<string>());
  useServerInsertedHTML(() => {
    const sheet = StyleSheet.getSheet();
    const fresh = splitRules(sheet.textContent).filter((rule) => !sent.current.has(rule));
    if (fresh.length === 0) return null;
    const first = sent.current.size === 0;
    for (const rule of fresh) sent.current.add(rule);
    return <style id={first ? sheet.id : undefined} data-rnw-more={first ? undefined : ''} dangerouslySetInnerHTML={{ __html: fresh.join('') }} />;
  });
  return null;
}

/**
 * The server writes react-native-web's stylesheet into the page once per streamed chunk, all with the same
 * id, and the browser adds every new rule (dark-mode colours, say) to the FIRST copy only. The later copies
 * carry the base rules (`.css-text { color: black }`) and, coming later in the page, beat the new rules, so
 * anything coloured only in dark mode turned black or transparent. Fold the copies into one.
 */
function useMergedStyleSheets() {
  useEffect(() => {
    const sheets = Array.from(document.querySelectorAll<HTMLStyleElement>('style[id="react-native-stylesheet"]'));
    if (sheets.length < 2) return;
    const [first, ...rest] = sheets;
    const target = first.sheet;
    if (!target) return;
    const have = new Set(Array.from(target.cssRules, (rule) => rule.cssText));
    for (const copy of rest) {
      for (const rule of Array.from(copy.sheet?.cssRules ?? [])) {
        if (have.has(rule.cssText)) continue;
        have.add(rule.cssText);
        try {
          target.insertRule(rule.cssText, target.cssRules.length);
        } catch {
          // A rule the browser cannot parse is skipped; the same rule is not needed twice.
        }
      }
      copy.remove();
    }
  }, []);
}

/**
 * react-native-web draws a Modal as role="dialog" aria-modal but gives it no name, so a screen reader says just
 * "dialog". Name each dialog after the first line of text inside it, which is its title in every Modal here.
 */
function useDialogNames() {
  useEffect(() => {
    const name = () => {
      document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]').forEach((dialog) => {
        if (dialog.hasAttribute('aria-label') || dialog.hasAttribute('aria-labelledby')) return;
        // An icon is a single glyph from the icon font, so the title is the first line with real letters or digits.
        const title = dialog.innerText.split(String.fromCharCode(10)).map((line) => line.trim()).find((line) => /[\p{L}\p{N}]/u.test(line));
        if (title) dialog.setAttribute('aria-label', title.slice(0, 80));
      });
    };
    name();
    const watcher = new MutationObserver(name);
    // react-native-web adds role="dialog" to the element after inserting it, so attribute changes count too.
    watcher.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'aria-modal'] });
    return () => watcher.disconnect();
  }, []);
}

const LazyBackground = lazy(() => import('./Background'));

/** True once the browser has nothing better to do (or after 4 seconds at most), so late work stays out of the first paint. */
function useIdle(): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    const idleWindow = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (idleWindow.requestIdleCallback) {
      const id = idleWindow.requestIdleCallback(() => setIdle(true), { timeout: 4000 });
      return () => idleWindow.cancelIdleCallback?.(id);
    }
    const timer = setTimeout(() => setIdle(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  return idle;
}

function Frame({ children }: { children: React.ReactNode }) {
  const { colors } = useAppTheme();
  return (
    <SafeAreaProvider style={{ backgroundColor: colors.paper }}>
      <Suspense fallback={null}>
        <RouterBridge />
      </Suspense>
      <ResponsiveFrame>
        <View style={{ flex: 1, backgroundColor: colors.paper }}>
          <AuthGuard>
            <Suspense fallback={null}>{children}</Suspense>
          </AuthGuard>
        </View>
        <ToastHost />
        <AlertHost />
        <InstallPrompt />
      </ResponsiveFrame>
    </SafeAreaProvider>
  );
}

function Session({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  useMergedStyleSheets();
  useDialogNames();
  useSyncGenderFilter();
  // An invite code waiting in this browser is applied once the person is signed in (a Google sign-up has no
  // verification screen to do it).
  useApplyPendingReferral();
  const idle = useIdle();

  useEffect(() => {
    // The saved theme and language are applied once preferences load; only then is the first paint shown
    // (see "app-pending" in customer.css), so a dark-mode or Bengali visitor never sees the light, English page.
    void dispatch(loadPreferences()).then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => document.documentElement.classList.add('app-ready')));
    });
    void dispatch(bootstrapAuth()).then(() => {
      dispatch(loadFavorites());
    });
  }, [dispatch]);


  // Lets the installed app open offline.
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <ThemeProvider>
      <Frame>{children}</Frame>
      {idle ? (
        <Suspense fallback={null}>
          <LazyBackground />
        </Suspense>
      ) : null}
    </ThemeProvider>
  );
}

export default function CustomerRoot({ children }: { children: React.ReactNode }) {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <ServerStyles />
      <Provider store={store} serverState={INITIAL_STATE}>
        <Session>{children}</Session>
      </Provider>
    </GestureHandlerRootView>
  );
}
